import React, { useCallback, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useTranslation } from "react-i18next";
import { TFunction } from "i18next/typescript/t";
import { SearchOutlined } from "@ant-design/icons";
import { useSelector } from "react-redux";

import { Roles, SharingRights, SharingRight } from "@/shared/utils/constants";
import {
  useGetLanguagesQuery,
  useLazyGetOrganizationsQuery,
  useLazyGetSectionsQuery,
  useLazyGetSurveysQuery,
  useLazyGetUsersQuery,
} from "@/redux/service";
import { FilterField } from "@/components_v2/Filters/types";
import FilterRenderer from "@/components_v2/Filters/FilterRenderer";
import { useFilterCount } from "@/shared/hooks/useFilterCount";
import {
  HookFormInput,
  HookFormBaseDropdown,
  HookFormInfiniteScrollDropdown,
} from "@/components_v2/FormFields/ReactHookForm/fields";
import { BaseUserResponse, UserQueryParams } from "@/redux/users/types";
import {
  OrganizationQueryParams,
  BaseOrganizationResponse,
} from "@/redux/organizations/types";
import { LanguageResponse } from "@/redux/common/types";
import {
  SectionQueryParams,
  SectionResponse,
  SurveyResponse,
} from "@/redux/survey/types";
import { isPaginatedResponse } from "@/redux/typeGuards";
import { useEvidenceTemplates } from "@/domains/evidence/hooks/useEvidenceTemplates";
import {
  EvidenceFiltersProps,
  EvidenceFiltersState,
} from "@/domains/evidence/types";
import { selectCurrentUser } from "@/redux/authentication/selectors";

const evidenceFiltersSchema = (
  t: TFunction,
): yup.ObjectSchema<Omit<EvidenceFiltersState, "user">> =>
  yup
    .object()
    .shape({
      ids: yup
        .array()
        .of(yup.number().required().typeError(t("form.validation.notValid")))
        .default([]),
      editable: yup.boolean().typeError(t("form.validation.notValid")),
      dataTemplates: yup
        .array()
        .of(
          yup
            .number()
            .required()
            .typeError(t("datablocks.errors.datablockTypeNotValid")),
        )
        .default([]),
      sharingRights: yup
        .array()
        .of(yup.string().required().typeError(t("form.validation.notValid")))
        .default([]),
      contributors: yup
        .array()
        .of(yup.number().required().typeError(t("form.validation.notValid")))
        .default([]),
      organizations: yup
        .array()
        .of(yup.number().required().typeError(t("form.validation.notValid")))
        .default([]),
      languages: yup
        .array()
        .of(yup.number().required().typeError(t("form.validation.notValid")))
        .default([]),
      sections: yup
        .array()
        .of(yup.number().required().typeError(t("form.validation.notValid")))
        .default([]),
      search: yup
        .string()
        .transform((value) => (value === "" ? undefined : value))
        .matches(/^[^<>*]+$/, t("datablocks.errors.specialCharsNotAllowed")),
    })
    .required();

const EvidenceFilters = ({
  onFiltersChange,
  filterNames,
  defaultFilters,
}: EvidenceFiltersProps) => {
  const { t } = useTranslation();
  const { data: languages } = useGetLanguagesQuery({ pagination: "off" }) as {
    data: LanguageResponse[];
  };
  const [getUsers] = useLazyGetUsersQuery();
  const [getOrganizations] = useLazyGetOrganizationsQuery();
  const [getSections] = useLazyGetSectionsQuery();
  const [getSurveys] = useLazyGetSurveysQuery();
  const [surveyMap, setSurveyMap] = useState<Record<number, SurveyResponse>>(
    {},
  );

  const handleSectionsFetched = useCallback(
    async (sections: SectionResponse[]) => {
      const newSurveyIds = Array.from(
        new Set(sections.map((s) => s.survey)),
      ).filter((id) => id && !surveyMap[id]);

      if (newSurveyIds.length === 0) return;

      const response = await getSurveys({
        pagination: "off",
        ids: newSurveyIds,
      });
      const data = response.data;
      if (data) {
        setSurveyMap((prev) => {
          const surveys = isPaginatedResponse(data) ? data.results : data;
          const newEntries = Object.fromEntries(surveys.map((s) => [s.id, s]));
          return { ...prev, ...newEntries };
        });
      }
    },
    [surveyMap, getSurveys],
  );

  const dataTemplates = useEvidenceTemplates();
  const currentUser = useSelector(selectCurrentUser);
  const form = useForm<EvidenceFiltersState>({
    resolver: yupResolver(evidenceFiltersSchema(t)),
    mode: "onChange",
    defaultValues: {
      dataTemplates: [],
      sharingRights: [],
      contributors: [],
      organizations: [],
      languages: [],
      sections: [],
      search: undefined,
      ...defaultFilters,
    },
  });
  const activeFiltersCount = useFilterCount<EvidenceFiltersState>(
    form,
    defaultFilters,
  );

  const dataTemplatesOptions = useMemo(() => {
    if (dataTemplates == null) return [];

    return dataTemplates.map((template) => ({
      label: template.title,
      value: template.id,
    }));
  }, [dataTemplates]);

  const languageOptions = useMemo(() => {
    if (!languages) return [];

    return [...languages]
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((language) => ({
        label: language.name,
        value: language.id,
      }));
  }, [languages]);

  const filters: Record<string, FilterField> = useMemo(() => {
    return {
      type: {
        name: "dataTemplates",
        isAdvancedFilter: true,
        label: t("datablocks.labels.dataTemplateType"),
        component: (
          <HookFormBaseDropdown
            name="dataTemplates"
            control={form.control}
            options={dataTemplatesOptions}
            multiple
            placeholder={t("datablocks.placeholders.dataTemplateType")}
            allowClear={true}
          />
        ),
      },
      sharingRights: {
        name: "sharingRights",
        isAdvancedFilter: true,
        label: t("datablocks.labels.sharingRights"),
        component: (
          <HookFormBaseDropdown
            name="sharingRights"
            control={form.control}
            options={[
              SharingRights.PRIVATE,
              SharingRights.PUBLIC,
              SharingRights.PRME_COMMUNITY,
            ].map((right: SharingRight) => ({
              label: right.label,
              value: right.value,
              className: right.className,
            }))}
            multiple
            placeholder={t("datablocks.placeholders.selectSharingRights")}
            allowClear={true}
          />
        ),
      },
      ...(currentUser && {
        createdBy: {
          name: "user",
          isAdvancedFilter: false,
          component: (
            <HookFormInfiniteScrollDropdown<BaseUserResponse, UserQueryParams>
              name="user"
              control={form.control}
              fetchFn={getUsers}
              labelFieldName="fullName"
              valueFieldName="id"
              searchFieldName="search"
              placeholder={t("datablocks.placeholders.createdBy")}
              defaultFilters={
                currentUser.role === Roles.ADMIN.value
                  ? {}
                  : {
                      organizations: currentUser.organization?.id,
                    }
              }
              allowClear={true}
            />
          ),
        },
        contributors: {
          name: "contributors",
          isAdvancedFilter: true,
          label: t("datablocks.labels.contributors"),
          component: (
            <HookFormInfiniteScrollDropdown<BaseUserResponse, UserQueryParams>
              name="contributors"
              control={form.control}
              fetchFn={getUsers}
              labelFieldName="fullName"
              valueFieldName="id"
              searchFieldName="search"
              multiple
              placeholder={t("datablocks.placeholders.selectContributors")}
              defaultFilters={
                currentUser.role === Roles.ADMIN.value
                  ? {}
                  : {
                      organizations: currentUser.organization?.id,
                    }
              }
              allowClear={true}
            />
          ),
        },
      }),
      organizations: {
        name: "organizations",
        isAdvancedFilter: true,
        label: t("datablocks.labels.signatories"),
        component: (
          <HookFormInfiniteScrollDropdown<
            BaseOrganizationResponse,
            OrganizationQueryParams
          >
            name="organizations"
            control={form.control}
            fetchFn={getOrganizations}
            labelFieldName="name"
            valueFieldName="id"
            searchFieldName="name"
            multiple
            placeholder={t("datablocks.placeholders.selectSignatories")}
            defaultFilters={
              currentUser?.organization?.id
                ? { exclude: [currentUser.organization.id] }
                : {}
            }
            allowClear={true}
          />
        ),
      },
      sections: {
        name: "sections",
        isAdvancedFilter: true,
        label: t("datablocks.labels.principles"),
        component: (
          <HookFormInfiniteScrollDropdown<SectionResponse, SectionQueryParams>
            name="sections"
            control={form.control}
            fetchFn={getSections}
            labelFieldName="title"
            valueFieldName="id"
            searchFieldName="title"
            multiple
            labelFormatter={(section) => {
              const survey = surveyMap[section.survey];
              return survey
                ? `${section.title} - ${survey.title}`
                : section.title;
            }}
            onFetchSuccess={handleSectionsFetched}
            placeholder={t("datablocks.placeholders.selectPrinciples")}
            defaultFilters={{ parentOnly: true, ordering: "-survey,index" }}
            allowClear={true}
          />
        ),
      },
      ...(languageOptions?.length > 0 && {
        languages: {
          name: "languages",
          isAdvancedFilter: true,
          label: t("datablocks.labels.languages"),
          component: (
            <HookFormBaseDropdown
              name="languages"
              control={form.control}
              options={languageOptions}
              multiple
              placeholder={t("datablocks.placeholders.selectLanguages")}
              allowClear={true}
            />
          ),
        },
      }),
      search: {
        name: "search",
        isAdvancedFilter: false,
        isDebounced: true,
        label: t("datablocks.labels.searchEvidence"),
        component: (
          <HookFormInput
            name="search"
            className="data-blocks-filters-search"
            suffix={<SearchOutlined />}
            placeholder={t("datablocks.placeholders.searchEvidence")}
            control={form.control}
          />
        ),
      },
    };
  }, [dataTemplatesOptions, languageOptions, currentUser, surveyMap]);

  return (
    <FilterRenderer<EvidenceFiltersState>
      filters={filters}
      activeFilterNames={filterNames}
      form={form}
      onFiltersChange={onFiltersChange}
      activeFilterCount={activeFiltersCount}
      popoverTitle={t("datablocks.labels.filters")}
      handleClear={() => {
        form.reset(defaultFilters);
        onFiltersChange(form.getValues());
      }}
      handleSearch={() => onFiltersChange(form.getValues())}
      className="data-blocks-filters"
    />
  );
};

export default EvidenceFilters;
